/**
 * Instrumentation #3 (CU-86d345rgy): tell the API that the lead-distribution opt-in toggle
 * was actually shown to the customer, so "saw the toggle but didn't opt in" is queryable per
 * region. Best-effort telemetry backed by an idempotent server-side upsert — it never throws,
 * so a failed/slow beacon can't block the booking form.
 */
export interface OfferShownHttp {
    post(url: string): Promise<unknown>;
}

/**
 * Whether the OFFER_SHOWN beacon should fire right now. The customer must be genuinely staying on
 * the customer-details step (i.e. past the onMounted redirect guard) with the opt-in toggle visible.
 * Firing during component setup — before that guard can bounce an incomplete/early visitor back to
 * inventory — produced phantom "offer shown" rows with no eligibility evaluation behind them, which
 * then mis-classified as "Declined" (CU-86d3hfvc3). One-shot: never re-fire once reported or while a
 * send is already in flight.
 */
export function shouldReportOfferShown(state: {
    stayingOnStep: boolean;
    optInVisible: boolean;
    alreadyReported: boolean;
    reporting: boolean;
}): boolean {
    return state.stayingOnStep && state.optInVisible && !state.alreadyReported && !state.reporting;
}

export async function recordOfferShown(http: OfferShownHttp, orderId: number | string | null): Promise<boolean> {
    if (!orderId) {
        return false;
    }

    try {
        await http.post(`/customers/book/orders/${orderId}/lead-distribution/offer-shown`);

        return true;
    } catch {
        // Telemetry only — swallow failures (a later render may retry).
        return false;
    }
}
